import React, {
    useEffect, useMemo, useReducer, useRef,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import { Alert, Form, Table } from 'react-bootstrap';
import {
    DialogContent,
    makeStyles,
} from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import Loading from 'components/widgets/Loading';
import CustomerService from 'services/modules/CustomerService';
import ModalUtils from 'utils/ModalUtils';
import {
    MERGE_FIELDS,
    NO_DATA,
    getFieldCompareValue,
    getFieldDisplayValue,
} from 'components/modules/customer/merge/mergeFieldConfig';
import MergeProgressModal from 'components/modules/customer/merge/MergeProgressModal';

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(2.5, 3, 2),
        overflow: 'auto',
        flex: 1,
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    warningBanner: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.25, 2),
        marginBottom: theme.spacing(2),
    },
    warningIcon: {
        color: 'inherit',
        marginTop: 2,
    },
    warningTitle: {
        fontWeight: 700,
        marginBottom: 2,
    },
    warningText: {
        fontSize: '0.875rem',
        marginBottom: 0,
    },
    tableWrap: {
        overflowX: 'auto',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 4,
    },
    table: {
        minWidth: 760,
        marginBottom: 0,
        '& th, & td': {
            verticalAlign: 'middle',
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
        },
    },
    fieldLabel: {
        fontWeight: 700,
        whiteSpace: 'nowrap',
        backgroundColor: theme.palette.common.white,
        width: 150,
        color: theme.palette.text.primary,
    },
    headerCell: {
        textTransform: 'uppercase',
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: 0.4,
        minWidth: 180,
        textAlign: 'left',
        verticalAlign: 'top',
    },
    masterHeader: {
        backgroundColor: theme.palette.primary.lighter,
        color: theme.palette.secondary.dark,
    },
    secondaryHeader: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.secondary,
    },
    badge: {
        display: 'inline-block',
        fontWeight: 700,
        marginBottom: 2,
    },
    idText: {
        display: 'inline-flex',
        alignItems: 'center',
        fontWeight: 500,
        textTransform: 'none',
        fontSize: '0.75rem',
        marginTop: 0,
        lineHeight: 1,
    },
    zebra: {
        backgroundColor: theme.palette.grey[50],
    },
    cell: {
        cursor: 'pointer',
        padding: 0,
    },
    masterColumn: {
        backgroundColor: theme.palette.action.hover,
    },
    selectedCell: {
        backgroundColor: theme.palette.action.selected,
    },
    disabledCell: {
        opacity: 0.4,
        backgroundColor: theme.palette.grey[200],
        cursor: 'not-allowed',
    },
    headerCheckbox: {
        marginRight: theme.spacing(0.5),
        marginTop: 0,
        marginBottom: 0,
        paddingLeft: 0,
        display: 'inline-flex',
        alignItems: 'center',
        '& .form-check-input': {
            position: 'static',
            margin: 0,
            cursor: 'pointer',
        },
        '& input': {
            cursor: 'pointer',
        },
    },
    radioCheck: {
        marginBottom: 0,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minHeight: '100%',
        padding: 0,
        '& .form-check-label': {
            cursor: 'pointer',
            marginBottom: 0,
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: theme.spacing(0.75, 1),
        },
        '& input': {
            position: 'static',
            cursor: 'pointer',
            marginTop: 0,
            marginLeft: theme.spacing(1),
        },
    },
    flexCenter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        lineHeight: 1,
        width: '100%',
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        width: '100%',
    },
    value: {
        wordBreak: 'break-word',
        fontSize: '0.875rem',
        lineHeight: 1.35,
        marginLeft: theme.spacing(0.5),
    },
    empty: {
        color: theme.palette.text.disabled,
        fontStyle: 'italic',
    },
    lastUpdateValue: {
        wordBreak: 'break-word',
        fontSize: '0.7rem',
        lineHeight: 1.35,
        color: theme.palette.text.secondary,
        marginTop: 4,
        textAlign: 'left',
        textTransform: 'none',
        fontWeight: 400,
        letterSpacing: 0,
    },
    matchValue: {
        fontSize: '0.7rem',
        lineHeight: 1.35,
        color: theme.palette.text.secondary,
        marginTop: 2,
        textAlign: 'left',
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: 0,
    },
}));

const DuplicateResolveStep = ({
    active,
    masterCustomerCode,
    secondaryCustomerCodes,
    suggestedCustomer,
    onClose,
    onMergeCompleted,
    onUiChange,
    confirmRequestRef,
    onRequestConfirm,
    executeMergeRef,
}) => {
    const classes = useStyles();
    const customerService = useMemo(() => new CustomerService(), []);

    const matchByCode = useMemo(() => {
        let list;
        if (Array.isArray(suggestedCustomer)) {
            list = suggestedCustomer;
        } else {
            list = suggestedCustomer ? [suggestedCustomer] : [];
        }
        return list.reduce((acc, item) => {
            if (item?.customerCode == null) return acc;
            if (item.matchPercentage == null) return acc;
            acc[Number(item.customerCode)] = item.matchPercentage;
            return acc;
        }, {});
    }, [suggestedCustomer]);

    const initialState = {
        loading: true,
        master: null,
        secondaries: [],
        selections: {},
        progressOpen: false,
        progressStatus: 'running',
        progressError: '',
        merging: false,
        includedSecondaries: {},
    };

    const [state, setState] = useReducer((old, action) => {
        const updates = typeof action === 'function' ? action(old) : action;
        return { ...old, ...updates };
    }, initialState);

    const mergeTimerRef = useRef(null);

    useEffect(() => () => {
        if (mergeTimerRef.current) {
            clearTimeout(mergeTimerRef.current);
        }
    }, []);

    const {
        loading,
        master,
        secondaries,
        selections,
        progressOpen,
        progressStatus,
        progressError,
        merging,
        includedSecondaries,
    } = state;

    const customers = useMemo(() => (master ? [master, ...secondaries] : []), [master, secondaries]);
    const activeSecondaries = useMemo(
        () => secondaries.filter((c) => includedSecondaries[Number(c.customerCode)] !== false),
        [secondaries, includedSecondaries],
    );
    const canMerge = !loading && Boolean(master) && activeSecondaries.length > 0;

    const buildPreviewRows = () => MERGE_FIELDS.map((field) => {
        const sourceCustomerCode = Number(selections[field.key]);
        const sourceCustomer = customers.find(
            (customer) => Number(customer.customerCode) === sourceCustomerCode,
        );
        const value = getFieldDisplayValue(field, sourceCustomer);
        return {
            key: field.key,
            label: field.label,
            value: value || NO_DATA,
            sourceCustomerCode,
            fromMaster: sourceCustomerCode === Number(masterCustomerCode),
        };
    });

    const buildFieldOverrides = () => MERGE_FIELDS
        .filter((field) => Number(selections[field.key]) !== Number(masterCustomerCode))
        .map((field) => ({
            field: field.overrideField,
            sourceCustomerCode: Number(selections[field.key]),
        }));

    const executeMerge = async () => {
        setState({
            progressOpen: true,
            progressStatus: 'running',
            progressError: '',
            merging: true,
        });

        const expectedTimestamps = (master ? [master, ...activeSecondaries] : []).map((c) => ({
            customerCode: Number(c.customerCode),
            modifiedOn: c.modifiedOn,
        }));

        const input = {
            masterCustomerCode: Number(masterCustomerCode),
            secondaryCustomerCodes: activeSecondaries.map((item) => Number(item.customerCode)),
            fieldOverrides: buildFieldOverrides(),
            expectedTimestamps,
        };

        try {
            const response = await customerService.mergeCustomers(input);
            const { data, graphQLErrors } = response;

            if (graphQLErrors) {
                const errMsg = graphQLErrors[0]?.message || 'Merge failed.';
                const isConcurrencyError = errMsg.includes('already been merged')
                    || errMsg.includes('modified by another user');

                if (isConcurrencyError) {
                    setState({ progressOpen: false });
                    onClose();
                    ModalUtils.errorMessage(graphQLErrors);
                    onMergeCompleted();
                    return;
                }

                setState({ progressStatus: 'error', progressError: errMsg });
                ModalUtils.errorMessage(graphQLErrors);
                return;
            }

            if (data?.mergeCustomers) {
                setState({ progressStatus: 'success' });
                mergeTimerRef.current = setTimeout(() => {
                    setState({ progressOpen: false });
                    onClose();
                    onMergeCompleted();
                }, 900);
            } else {
                setState({ progressStatus: 'error', progressError: 'Merge failed.' });
            }
        } catch (error) {
            setState({ progressStatus: 'error', progressError: error?.message || 'Merge failed.' });
        } finally {
            setState({ merging: false });
        }
    };

    const requestConfirm = () => {
        if (!canMerge || !onRequestConfirm) return;
        onRequestConfirm({
            secondaryCount: activeSecondaries.length,
            previewRows: buildPreviewRows(),
        });
    };

    useEffect(() => {
        if (!onUiChange) return;
        onUiChange({
            loading,
            ready: !loading && Boolean(master),
            canMerge,
            merging,
        });
    }, [loading, master, canMerge, merging, onUiChange]);

    if (confirmRequestRef) {
        // Keep latest callback on the shared ref without re-subscribing effects.
        // eslint-disable-next-line no-param-reassign
        confirmRequestRef.current = requestConfirm;
    }
    if (executeMergeRef) {
        // eslint-disable-next-line no-param-reassign
        executeMergeRef.current = executeMerge;
    }

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setState({ loading: true });
            try {
                const codes = [masterCustomerCode, ...secondaryCustomerCodes];
                const responses = await Promise.all(
                    codes.map((code) => customerService.getMergedCustomerInfo(code)),
                );

                if (!mounted) return;

                const errors = responses.flatMap((response) => {
                    if (response instanceof Error) return [response];
                    if (response?.graphQLErrors?.length) return response.graphQLErrors;
                    return [];
                });
                if (errors.length > 0) {
                    ModalUtils.errorMessage(errors);
                    onClose();
                    return;
                }

                const records = responses
                    .map((response) => response.data?.getMergedCustomerInfo)
                    .filter(Boolean);

                if (records.length !== codes.length) {
                    ModalUtils.errorMessage([{ message: 'Could not load all selected customer records. Please try again.' }]);
                    onClose();
                    return;
                }
                const masterRecord = records.find(
                    (item) => Number(item.customerCode) === Number(masterCustomerCode),
                );
                const secondaryRecords = records.filter(
                    (item) => Number(item.customerCode) !== Number(masterCustomerCode),
                );

                if (!masterRecord) {
                    ModalUtils.errorMessage([{ message: 'Master customer could not be loaded.' }]);
                    onClose();
                    return;
                }

                const initialSelections = {};
                MERGE_FIELDS.forEach((field) => {
                    initialSelections[field.key] = Number(masterCustomerCode);
                });

                const initialIncluded = {};
                secondaryRecords.forEach((c) => {
                    initialIncluded[Number(c.customerCode)] = true;
                });

                setState({
                    master: masterRecord,
                    secondaries: secondaryRecords,
                    selections: initialSelections,
                    includedSecondaries: initialIncluded,
                });
            } finally {
                if (mounted) setState({ loading: false });
            }
        };

        if (active) load();

        return () => {
            mounted = false;
        };
    }, [active, masterCustomerCode, secondaryCustomerCodes, customerService, onClose]);

    const onSelectField = (fieldKey, customerCode) => {
        setState((prev) => ({
            selections: {
                ...prev.selections,
                [fieldKey]: Number(customerCode),
            },
        }));
    };

    const onToggleSecondary = (customerCode) => {
        const codeNum = Number(customerCode);
        setState((prevState) => {
            const newState = !prevState.includedSecondaries[codeNum];
            let newSelections = prevState.selections;

            if (!newState) {
                newSelections = { ...newSelections };
                Object.keys(newSelections).forEach((fieldKey) => {
                    if (newSelections[fieldKey] === codeNum) {
                        newSelections[fieldKey] = Number(masterCustomerCode);
                    }
                });
            }

            return {
                includedSecondaries: {
                    ...prevState.includedSecondaries,
                    [codeNum]: newState,
                },
                selections: newSelections,
            };
        });
    };

    const formatLastUpdate = (customer) => {
        if (customer?.modifiedOn) {
            return `Updated ${moment(customer.modifiedOn).fromNow()}`;
        }
        if (customer?.sysDate) {
            return `Created ${moment(customer.sysDate).fromNow()}`;
        }
        return NO_DATA;
    };

    const renderHeader = (customer, isMaster) => {
        const isIncluded = isMaster || includedSecondaries[Number(customer.customerCode)] !== false;
        const lastUpdate = formatLastUpdate(customer);
        const isEmpty = lastUpdate === NO_DATA;
        const matchPercentage = matchByCode[Number(customer.customerCode)];

        return (
            <th
                key={customer.customerCode}
                className={clsx(
                    classes.headerCell,
                    isMaster ? classes.masterHeader : classes.secondaryHeader,
                )}
            >
                <span className={classes.badge}>
                    {isMaster ? 'Master (Surviving)' : 'Secondary (Archived)'}
                </span>
                <div className={classes.flexColumn}>
                    <div className={classes.flexCenter}>
                        {!isMaster && (
                            <Form.Check
                                type="checkbox"
                                checked={isIncluded}
                                onChange={() => onToggleSecondary(customer.customerCode)}
                                className={classes.headerCheckbox}
                                aria-label={`Include customer ${customer.customerCode}`}
                            />
                        )}
                        <span className={classes.idText}>
                            {isMaster ? `#${customer.customerCode}` : (
                                <a
                                    href={`/customers/${customer.customerCode}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    #
                                    {customer.customerCode}
                                </a>
                            )}
                        </span>
                    </div>
                    <div className={clsx(classes.lastUpdateValue, isEmpty && classes.empty)}>
                        {lastUpdate}
                    </div>
                    {!isMaster && matchPercentage != null && (
                        <div className={classes.matchValue}>
                            {`${matchPercentage}% match`}
                        </div>
                    )}
                </div>
            </th>
        );
    };

    return (
        <>
            <DialogContent className={classes.content}>
                <div className={classes.subtitle}>
                    Review differences and select the preferred data points to construct the final Master Record.
                </div>

                <Alert variant="warning" className={classes.warningBanner}>
                    <WarningOutlinedIcon className={classes.warningIcon} fontSize="small" />
                    <div>
                        <div className={classes.warningTitle}>
                            <strong>Warning: Merging records is permanent</strong>
                        </div>
                        <div className={classes.warningText}>
                            Relational data (Customer, Deals, Opportunities, Service, SMS, Email,
                            Appointment, Credit Reporting, Document, Notes Records) will be consolidated
                            into the Master record. This action cannot be undone.
                        </div>
                    </div>
                </Alert>

                {loading && <Loading />}
                {!loading && master && (
                    <div className={classes.tableWrap}>
                        <Table striped bordered hover size="sm" className={classes.table}>
                            <thead>
                                <tr>
                                    <th className={clsx(classes.fieldLabel, classes.headerCell)}>
                                        Data Field
                                    </th>
                                    {customers.map((customer, index) => renderHeader(customer, index === 0))}
                                </tr>
                            </thead>
                            <tbody>
                                {MERGE_FIELDS.map((field, rowIndex) => (
                                    <tr
                                        key={field.key}
                                        className={rowIndex % 2 === 0 ? classes.zebra : undefined}
                                    >
                                        <td className={classes.fieldLabel}>{field.label}</td>
                                        {customers.map((customer, colIndex) => {
                                            const selected = Number(selections[field.key])
                                                === Number(customer.customerCode);
                                            const displayValue = getFieldDisplayValue(field, customer);
                                            const compareValue = getFieldCompareValue(field, customer);
                                            const isEmpty = !String(compareValue || '').trim();

                                            const isMaster = colIndex === 0;
                                            const isIncluded = isMaster
                                                || includedSecondaries[Number(customer.customerCode)] !== false;

                                            return (
                                                <td
                                                    key={`${field.key}-${customer.customerCode}`}
                                                    className={clsx(
                                                        classes.cell,
                                                        isMaster && classes.masterColumn,
                                                        selected && classes.selectedCell,
                                                        !isIncluded && classes.disabledCell,
                                                    )}
                                                >
                                                    <Form.Check
                                                        type="radio"
                                                        id={`field-${field.key}-${customer.customerCode}`}
                                                        name={`field-${field.key}`}
                                                        checked={selected}
                                                        disabled={!isIncluded}
                                                        className={classes.radioCheck}
                                                        onChange={() => {
                                                            if (isIncluded) {
                                                                onSelectField(field.key, customer.customerCode);
                                                            }
                                                        }}
                                                        label={(
                                                            <span className={clsx(classes.value, isEmpty && classes.empty)}>
                                                                {isEmpty ? NO_DATA : displayValue}
                                                            </span>
                                                        )}
                                                    />
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </DialogContent>

            {progressOpen && (
                <MergeProgressModal
                    open={progressOpen}
                    secondaries={activeSecondaries}
                    status={progressStatus}
                    errorMessage={progressError}
                    onClose={() => {
                        setState({
                            progressOpen: false,
                            progressError: '',
                            progressStatus: 'running',
                        });
                    }}
                />
            )}
        </>
    );
};

DuplicateResolveStep.propTypes = {
    active: PropTypes.bool.isRequired,
    masterCustomerCode: PropTypes.number.isRequired,
    secondaryCustomerCodes: PropTypes.arrayOf(PropTypes.number).isRequired,
    suggestedCustomer: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object),
    ]),
    onClose: PropTypes.func.isRequired,
    onMergeCompleted: PropTypes.func,
    onUiChange: PropTypes.func,
    confirmRequestRef: PropTypes.shape({ current: PropTypes.any }),
    onRequestConfirm: PropTypes.func,
    executeMergeRef: PropTypes.shape({ current: PropTypes.any }),
};

DuplicateResolveStep.defaultProps = {
    suggestedCustomer: [],
    onMergeCompleted: () => {},
    onUiChange: null,
    confirmRequestRef: null,
    onRequestConfirm: null,
    executeMergeRef: null,
};

export default DuplicateResolveStep;
